import { Link } from 'react-router';
import {
  CUISINE_LABELS,
  type Cuisine,
  type PlannerDayDto,
  type PlannerPickDto,
} from '@fewerbags/schema';
import { costLine, formatDollars, mealWarnings } from '../../lib/autoplan';
import { formatDayLong } from '../../lib/planDates';
import { formatClock, formatInstant, formatMinutes } from '../../lib/planner';
import { LEFTOVERS_LABEL, planTitle, reasonChipClass, SLOT_LABEL } from '../../lib/plans';
import { RecipePhoto } from '../recipes/RecipePhoto';
import { RatingSummary } from '../recipes/StarRating';

const actionButton =
  'min-h-11 rounded-xl border border-line bg-surface px-3.5 text-sm font-semibold text-brand transition-[background-color,transform] hover:bg-brand-soft active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100';

function cuisineLabel(c: string): string {
  return CUISINE_LABELS[c as Cuisine] ?? c;
}

function PickMeta({ pick }: { pick: PlannerPickDto }) {
  const { recipe } = pick;
  const time = formatMinutes(recipe.totalMin);
  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
      {time ? <span>{time}</span> : <span>time not set</span>}
      {recipe.cuisines.slice(0, 2).map((c) => (
        <span key={c} className="rounded-full bg-orange-soft px-2 py-0.5 text-orange-ink">
          {cuisineLabel(c)}
        </span>
      ))}
      <RatingSummary avg={recipe.householdRating.avg} count={recipe.householdRating.count} />
      {!recipe.own ? (
        <span className="rounded-full bg-accent-soft px-2 py-0.5 text-accent-ink">Shared</span>
      ) : null}
    </p>
  );
}

/**
 * Planner v2 extras (auto-plan wizard, D33): kind badge, place, who eats,
 * cost, warnings, dine-out and packed-leftovers bodies. The D29 page leaves
 * this out and renders exactly as before.
 */
export interface PlannerCardDetail {
  personName: (personId: string) => string;
  guestNames: readonly string[];
  /** Heading shows the slot only (the page groups cards under a date). */
  slotHeading?: boolean;
}

function AttendeeLine({ day, detail }: { day: PlannerDayDto; detail: PlannerCardDetail }) {
  const a = day.attendees;
  if (!a) return null;
  const guests = detail.guestNames.slice(0, a.guests);
  while (guests.length < a.guests) guests.push(`Guest ${guests.length + 1}`);
  const chips = a.everyone ? ['Everyone'] : [...a.personIds.map(detail.personName), ...guests];
  return (
    <ul aria-label="Who eats" className="flex flex-wrap gap-1">
      {chips.map((name, i) => (
        <li key={`${name}-${i}`} className="rounded-full bg-cream px-2 py-0.5 text-xs text-ink">
          {name}
        </li>
      ))}
      {day.servings !== null ? (
        <li className="px-1 py-0.5 text-xs text-muted">
          {day.servings} {day.servings === 1 ? 'serving' : 'servings'}
        </li>
      ) : null}
    </ul>
  );
}

function kindBadge(day: PlannerDayDto): string | null {
  if (day.kind === 'dine_out') return 'Dining out';
  if (day.kind === 'packed') return day.place ? `Packed · ${day.place.name}` : 'Packed';
  return null;
}

/** One day of the "Plan my week" board: cook window, proposal, reasons and actions. */
export function PlannerDayCard({
  day,
  timezone,
  userLocked,
  busy,
  onSwap,
  onToggleLock,
  onSkip,
  onRestore,
  detail,
}: {
  day: PlannerDayDto;
  timezone: string;
  /** Locked by the user (as opposed to pinned while swapping another day). */
  userLocked: boolean;
  busy: boolean;
  onSwap: () => void;
  onToggleLock: () => void;
  onSkip: () => void;
  onRestore: () => void;
  detail?: PlannerCardDetail | undefined;
}) {
  const main = day.main;
  const chips = main
    ? [...main.reasons]
        .filter((r) => r.kind !== 'window')
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
        .slice(0, 2)
    : [];
  const headingId = `planner-day-${day.date}-${day.slot}`;
  const dineOut = detail && day.kind === 'dine_out' && day.skipped === null;
  const leftovers = detail && !main && day.leftoversOf !== null && day.skipped === null;
  const badge = detail ? kindBadge(day) : null;
  const warnings = detail ? mealWarnings(day) : [];
  const cost = detail && day.skipped === null && !day.existingPlan ? costLine(day.cost) : null;
  // The cook-window badge only matters for a day we're proposing something for.
  const showWindow =
    !dineOut &&
    !leftovers &&
    !day.existingPlan &&
    day.skipped?.kind !== 'skipped' &&
    day.skipped?.kind !== 'busy' &&
    day.skipped?.kind !== 'no_usual_time';

  return (
    <li
      aria-labelledby={headingId}
      className={`card flex flex-col gap-3 overflow-hidden p-4 ${
        userLocked ? 'ring-2 ring-brand' : ''
      }`}
    >
      <div className="-mx-4 -mt-4 flex items-start justify-between gap-2 border-b border-line bg-brand-soft/50 px-4 py-3">
        <div>
          <h2 id={headingId} className="text-base font-semibold">
            {detail?.slotHeading ? SLOT_LABEL[day.slot] : formatDayLong(day.date)}
          </h2>
          <p className="text-xs font-medium text-muted">
            {detail?.slotHeading ? 'At' : `${SLOT_LABEL[day.slot]} at`} {formatClock(day.serveAt)}
            {day.servePushed ? ` (usually ${formatClock(day.usualServeAt)})` : ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {userLocked ? (
            <span className="rounded-full bg-brand px-2.5 py-0.5 text-xs font-semibold text-white shadow-sm">
              Locked
            </span>
          ) : null}
          {badge ? (
            <span className="rounded-full bg-surface px-2.5 py-0.5 text-xs font-semibold text-ink shadow-sm">
              {badge}
            </span>
          ) : null}
        </div>
      </div>

      {detail && !day.existingPlan && day.skipped?.kind !== 'skipped' ? (
        <AttendeeLine day={day} detail={detail} />
      ) : null}

      {!showWindow ? null : day.busyNote ? (
        <p className="rounded-xl bg-orange-soft px-3 py-1.5 text-xs font-semibold text-orange-ink">
          {day.busyNote}
        </p>
      ) : (
        <p className="rounded-xl bg-accent-soft px-3 py-1.5 text-xs font-semibold text-accent-ink">
          No time limit — any recipe fits
        </p>
      )}

      {day.busy.length > 0 && !day.existingPlan ? (
        <ul className="flex flex-col gap-0.5 text-xs text-muted">
          {day.busy.map((b) => (
            <li key={`${b.start}-${b.label}`}>
              {b.label}: {formatInstant(b.start, timezone)}–{formatInstant(b.end, timezone)}
              {b.source === 'calendar' ? ' (calendar)' : ''}
            </li>
          ))}
        </ul>
      ) : null}

      {day.existingPlan ? (
        <div className="rounded-xl border border-line bg-cream p-3">
          <p className="text-xs font-semibold tracking-wide text-muted uppercase">
            Already planned
          </p>
          <Link
            to={`/plan/${day.existingPlan.id}`}
            className="mt-1 block text-sm font-semibold text-brand underline-offset-2 hover:underline"
          >
            {planTitle(day.existingPlan)}
          </Link>
        </div>
      ) : main ? (
        <>
          <div className="flex gap-3">
            <RecipePhoto
              photoMediaId={main.recipe.photoMediaId}
              title={main.recipe.title}
              variant="thumb"
              cuisines={main.recipe.cuisines as Cuisine[]}
              className="size-24 shrink-0 rounded-xl shadow-sm"
            />
            <div className="min-w-0 flex-1">
              <Link
                to={`/recipes/${main.recipe.recipeId}`}
                className="text-base leading-snug font-semibold text-brand underline-offset-2 hover:underline"
              >
                {main.recipe.title}
              </Link>
              <PickMeta pick={main} />
            </div>
          </div>
          {day.sides.length > 0 ? (
            <ul className="flex flex-col gap-1 border-t border-line pt-2">
              {day.sides.map((side) => (
                <li key={side.recipe.recipeId} className="text-sm">
                  <span className="text-muted">With </span>
                  <Link
                    to={`/recipes/${side.recipe.recipeId}`}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    {side.recipe.title}
                  </Link>
                  {side.recipe.totalMin !== null ? (
                    <span className="text-xs text-muted">
                      {' '}
                      · {formatMinutes(side.recipe.totalMin)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {chips.length > 0 ? (
            <ul className="flex flex-wrap gap-1">
              {chips.map((r, i) => (
                <li
                  key={`${r.kind}-${i}`}
                  className={`rounded-full px-2 py-0.5 text-xs ${reasonChipClass(r.delta)}`}
                >
                  {r.text}
                </li>
              ))}
            </ul>
          ) : null}
          {main.reasons.length > chips.length ? (
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold text-brand">Why this?</summary>
              <ul className="mt-1 flex flex-col gap-0.5 text-muted">
                {main.reasons.map((r, i) => (
                  <li key={`${r.kind}-${i}`}>
                    {r.delta > 0 ? '+ ' : r.delta < 0 ? '− ' : '· '}
                    {r.text}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      ) : dineOut ? (
        <div className="rounded-xl border border-line bg-cream p-3">
          <p className="text-base font-semibold">{day.dineOut?.note || 'Dining out'}</p>
          <p className="text-xs text-muted">
            No cooking, no groceries
            {day.dineOut ? ` · about ${formatDollars(day.dineOut.estCostCents)}` : ''}
          </p>
        </div>
      ) : leftovers && day.leftoversOf ? (
        <div className="rounded-xl border border-line bg-cream p-3">
          <p className="text-xs font-semibold tracking-wide text-muted uppercase">
            {LEFTOVERS_LABEL}
          </p>
          <p className="mt-0.5 text-base font-semibold">{day.leftoversOf.title}</p>
          <p className="text-xs text-muted">
            From {formatDayLong(day.leftoversOf.date)}{' '}
            {SLOT_LABEL[day.leftoversOf.slot].toLowerCase()}
          </p>
        </div>
      ) : day.skipped ? (
        <p className="text-sm text-muted">{day.skipped.text}</p>
      ) : null}

      {cost && !dineOut ? <p className="text-xs font-semibold text-ink">{cost}</p> : null}
      {warnings.length > 0 ? (
        <ul aria-label="Warnings" className="flex flex-col gap-1">
          {warnings.map((w, i) => (
            <li
              key={`${w.kind}-${i}`}
              className="rounded-lg bg-orange-soft px-2.5 py-1 text-xs font-medium text-orange-ink"
            >
              {w.text}
            </li>
          ))}
        </ul>
      ) : null}

      {!day.existingPlan ? (
        <div className="mt-auto flex flex-wrap gap-2">
          {day.skipped?.kind === 'skipped' ? (
            <button type="button" className={actionButton} onClick={onRestore} disabled={busy}>
              Plan this day
            </button>
          ) : day.skipped?.kind === 'no_usual_time' ? null : (
            <>
              {main ? (
                <>
                  <button
                    type="button"
                    className={actionButton}
                    onClick={onSwap}
                    disabled={busy || userLocked}
                    aria-label={`Swap ${main.recipe.title} for something else`}
                  >
                    Swap
                  </button>
                  <button
                    type="button"
                    className={actionButton}
                    onClick={onToggleLock}
                    disabled={busy}
                    aria-pressed={userLocked}
                  >
                    {userLocked ? 'Unlock' : 'Lock'}
                  </button>
                </>
              ) : null}
              <button type="button" className={actionButton} onClick={onSkip} disabled={busy}>
                {detail ? 'Skip' : 'Skip day'}
              </button>
            </>
          )}
        </div>
      ) : null}
    </li>
  );
}
